import React, { useEffect, useState } from "react";
import BottomSheet from "./BottomSheet";
import FanAvatar from "./FanAvatar";
import InstagramGlyph from "./InstagramGlyph";
import LevelRing from "./LevelRing";
import ProfileMeta from "./ProfileMeta";
import VerifiedTick from "./VerifiedTick";
import { FanRow } from "./FansLeaderboardSheet";
import { openExternal } from "../lib/openExternal";
import { ordersApi } from "../services/orders";
import type { FanCard } from "../types/fans";
import { t } from "../i18n";
import "./FanProfileHero.css";
import "./FanProfileSheet.css";
import "./FanCardSheet.css";

// Another fan's card (founder, 2026-11-03): a tap on them on Top Fans. Their
// photo and level, name, Instagram when there is one, country, when they joined,
// and their requests, points and played. Never their songs, DJs or venues: that
// would show where someone was. The row's own data shows at once; the rest follows.

const FanCardSheet: React.FC<{ fan: FanRow | null; onClose: () => void }> = ({ fan, onClose }) => {
  const id = fan?.fanUserId;
  const [card, setCard] = useState<FanCard | null>(null);
  useEffect(() => {
    setCard(null);
    if (!id) return;
    let cancelled = false;
    ordersApi
      .fanCard(id)
      .then((res) => {
        if (!cancelled) setCard(res);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id]);
  if (!fan) return null;
  const level = card?.level ?? fan.level ?? 0;
  const ig = card ? card.ig : fan.ig;
  const verified = card ? card.igVerified : fan.igVerified;
  const reportHref = `mailto:hello@requesta.app?subject=${encodeURIComponent("Report a fan profile")}&body=${encodeURIComponent(`Profile: ${fan.name}\nWhy:`)}`;
  return (
    <BottomSheet open title={fan.name} onClose={onClose} plain>
      <div className="fcard">
        <LevelRing level={level} hideNumber>
          <FanAvatar className="fps-avatar fph-avatar" name={fan.name} avatarUrl={fan.avatarUrl} />
        </LevelRing>
        <div className="fcard-name">
          <span className="fcard-name-text">{fan.name}</span>
          {verified && <VerifiedTick className="fps-tick" label={t("fanConnect.verified")} />}
        </div>
        {ig && (
          <button type="button" className="fcard-ig" onClick={() => void openExternal(`https://instagram.com/${encodeURIComponent(ig)}`)}>
            <InstagramGlyph size={15} />@{ig}
          </button>
        )}
        <ProfileMeta countryCode={card?.countryCode} joinedAt={card?.joinedAt} />
        <div className="fph-stats fcard-stats">
          <span className="fph-count">
            <b>{(card?.requests ?? fan.requests).toLocaleString()}</b>
            {t("bottomTabBar.requests")}
          </span>
          <div className={`fph-points fcard-points fph-points--${Math.min(level, 5)}`}>
            <span className="fph-points-num">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.2l-6.1 3.4 1.4-6.8L2.2 9.1l6.9-.8z" />
              </svg>
              {card ? t("fanProfileEditorSheet.pts", { points: card.points.toLocaleString() }).trim() : "…"}
            </span>
            {level > 0 && <span className="fph-points-level">{t("fanProfilePoints.level", { level })}</span>}
          </div>
          <span className="fph-count">
            <b>{card ? card.played.toLocaleString() : "…"}</b>
            {t("fanStatusPill.played")}
          </span>
        </div>
        <a className="fcard-report" href={reportHref}>{t("fanCard.report")}</a>
      </div>
    </BottomSheet>
  );
};

export default FanCardSheet;
